/**
 * Member Data Validation Component
 *
 * Provides UI for validating and enriching member data using external sources:
 * - USPS Address Validation
 * - Phone Lookup (Twilio)
 * - Email Validation
 * - Lexis-Nexis Identity Verification
 *
 * Trust Marketplace - Member Retention Intelligence
 */

import React, { useState } from 'react';
import {
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Home,
  Phone,
  Mail,
  User,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  BadgeCheck,
  MapPin,
  Shield,
  Copy,
  Pencil,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getApiBaseUrl } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authFetch } from '@/lib/authFetch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface MemberInfo {
  member_id: number;
  first_name: string;
  last_name: string;
  phone_number?: string;
  email?: string;
  address?: {
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zip_code: string;
  };
  date_of_birth?: string;
  ssn_last4?: string;
}

interface ValidationResult {
  source: string;
  is_valid: boolean;
  confidence_score: number;
  validated_at: string;
  details?: Record<string, any>;
  standardized_data?: Record<string, any>;
  warnings?: string[];
  errors?: string[];
}

interface MemberDataValidationProps {
  member: MemberInfo;
  compact?: boolean;
  onDataUpdated?: (field: string, newValue: any) => void;
}

// Status color mapping
const getStatusColor = (isValid: boolean, confidence: number): string => {
  if (!isValid) return 'destructive';
  if (confidence >= 0.9) return 'default';
  if (confidence >= 0.7) return 'warning';
  return 'destructive';
};

// Status icon
const getStatusIcon = (isValid: boolean, confidence: number) => {
  if (!isValid) return <AlertCircle className="h-5 w-5 text-destructive" />;
  if (confidence >= 0.9) return <CheckCircle className="h-5 w-5 text-green-600" />;
  if (confidence >= 0.7) return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
  return <AlertCircle className="h-5 w-5 text-destructive" />;
};

export const MemberDataValidation: React.FC<MemberDataValidationProps> = ({
  member,
  compact = false,
  onDataUpdated,
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [results, setResults] = useState<Record<string, ValidationResult | null>>({});
  const [expanded, setExpanded] = useState(!compact);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<{ field: string; value: any } | null>(null);
  const [validatingAll, setValidatingAll] = useState(false);

  // Validate address via USPS
  const validateAddress = async () => {
    if (!member.address || !token) return;

    setLoading((prev) => ({ ...prev, address: true }));

    try {
      const response = await authFetch(`${getApiBaseUrl()}/api/data-validation/validate/address`, {
        method: 'POST',
        jsonBody: {
          street1: member.address.street1,
          street2: member.address.street2,
          city: member.address.city,
          state: member.address.state,
          zip_code: member.address.zip_code,
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResults((prev) => ({
          ...prev,
          address: {
            source: 'usps',
            is_valid: data.is_valid,
            confidence_score: data.confidence_score,
            validated_at: data.validated_at,
            standardized_data: data.standardized_address,
            warnings: data.dpv_match !== 'Y' ? ['Address may not be deliverable'] : [],
          },
        }));
      }
    } catch (err) {
      console.error('Address validation error:', err);
      setResults((prev) => ({
        ...prev,
        address: {
          source: 'usps',
          is_valid: false,
          confidence_score: 0,
          validated_at: new Date().toISOString(),
          errors: ['Validation service unavailable'],
        },
      }));
    } finally {
      setLoading((prev) => ({ ...prev, address: false }));
    }
  };

  // Validate phone via Twilio
  const validatePhone = async () => {
    if (!member.phone_number || !token) return;

    setLoading((prev) => ({ ...prev, phone: true }));

    try {
      const response = await authFetch(`${getApiBaseUrl()}/api/data-validation/validate/phone`, {
        method: 'POST',
        jsonBody: {
          phone_number: member.phone_number,
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResults((prev) => ({
          ...prev,
          phone: {
            source: 'twilio',
            is_valid: data.is_valid,
            confidence_score: data.confidence_score,
            validated_at: data.validated_at,
            details: {
              carrier: data.carrier,
              line_type: data.line_type,
              country_code: data.country_code,
            },
            warnings:
              data.line_type === 'voip'
                ? ['VOIP number detected - may be less reliable']
                : undefined,
          },
        }));
      }
    } catch (err) {
      console.error('Phone validation error:', err);
      setResults((prev) => ({
        ...prev,
        phone: {
          source: 'twilio',
          is_valid: false,
          confidence_score: 0,
          validated_at: new Date().toISOString(),
          errors: ['Validation service unavailable'],
        },
      }));
    } finally {
      setLoading((prev) => ({ ...prev, phone: false }));
    }
  };

  // Validate email
  const validateEmail = async () => {
    if (!member.email || !token) return;

    setLoading((prev) => ({ ...prev, email: true }));

    try {
      const response = await authFetch(`${getApiBaseUrl()}/api/data-validation/validate/email`, {
        method: 'POST',
        jsonBody: {
          email: member.email,
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResults((prev) => ({
          ...prev,
          email: {
            source: 'email_validation',
            is_valid: data.is_valid,
            confidence_score: data.confidence_score,
            validated_at: data.validated_at,
            details: {
              domain_valid: data.domain_valid,
              syntax_valid: data.syntax_valid,
              mx_records: data.mx_records_found,
              is_disposable: data.is_disposable,
            },
            warnings: data.is_disposable ? ['Disposable email detected'] : undefined,
          },
        }));
      }
    } catch (err) {
      console.error('Email validation error:', err);
      setResults((prev) => ({
        ...prev,
        email: {
          source: 'email_validation',
          is_valid: false,
          confidence_score: 0,
          validated_at: new Date().toISOString(),
          errors: ['Validation service unavailable'],
        },
      }));
    } finally {
      setLoading((prev) => ({ ...prev, email: false }));
    }
  };

  // Validate all member data
  const validateAll = async () => {
    setValidatingAll(true);
    try {
      await Promise.all([
        member.address ? validateAddress() : Promise.resolve(),
        member.phone_number ? validatePhone() : Promise.resolve(),
        member.email ? validateEmail() : Promise.resolve(),
      ]);
    } finally {
      setValidatingAll(false);
    }
  };

  // Handle accepting standardized data
  const handleAcceptStandardized = (field: string, value: any) => {
    setPendingUpdate({ field, value });
    setShowUpdateDialog(true);
  };

  const confirmUpdate = async () => {
    if (!pendingUpdate || !token) return;

    try {
      // Call the backend API to update member data
      const response = await authFetch(`/api/data-validation/member/${member.member_id}/update`, {
        method: 'PUT',
        jsonBody: {
          field: pendingUpdate.field,
          value: pendingUpdate.value,
          validation_source: results[pendingUpdate.field]?.source || 'unknown',
          confidence_score: results[pendingUpdate.field]?.confidence_score || 0.8,
        }
      });

      if (response.ok) {
        // Notify parent component of the update
        if (onDataUpdated) {
          onDataUpdated(pendingUpdate.field, pendingUpdate.value);
        }
      } else {
        console.error('Failed to update member data:', await response.text());
      }
    } catch (err) {
      console.error('Error updating member data:', err);
    }

    setShowUpdateDialog(false);
    setPendingUpdate(null);
  };

  // Calculate overall validation status
  const getOverallStatus = () => {
    const validations = Object.values(results).filter((r) => r !== null);
    if (validations.length === 0) return null;

    const allValid = validations.every((r) => r?.is_valid);
    const avgConfidence =
      validations.reduce((sum, r) => sum + (r?.confidence_score || 0), 0) / validations.length;

    return { allValid, avgConfidence };
  };

  const overallStatus = getOverallStatus();

  return (
    <Card className="border">
      <CardContent className={compact ? 'pb-2' : 'pb-4'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BadgeCheck className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold">Data Validation</h3>
            {overallStatus && (
              <Badge
                variant={overallStatus.allValid ? 'default' : 'secondary'}
                className={
                  overallStatus.allValid
                    ? 'bg-v2accent hover:bg-v2accent/90'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }
              >
                {overallStatus.allValid
                  ? `${Math.round(overallStatus.avgConfidence * 100)}% Verified`
                  : 'Issues Found'}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={validateAll}
              disabled={validatingAll}
            >
              {validatingAll ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Validate All
            </Button>
            {compact && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>

        {expanded && (
          <div className="space-y-1">
            {/* Address Validation */}
            {member.address && (
              <>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-0.5">
                      {results.address ? (
                        getStatusIcon(results.address.is_valid, results.address.confidence_score)
                      ) : (
                        <Home className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">Address</p>
                      <p className="text-sm text-muted-foreground">
                        {member.address.street1}, {member.address.city}, {member.address.state}{' '}
                        {member.address.zip_code}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={validateAddress}
                    disabled={loading.address}
                  >
                    {loading.address ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <MapPin className="h-4 w-4 mr-2" />
                    )}
                    {results.address ? 'Re-validate' : 'Validate'}
                  </Button>
                </div>

                {results.address && (
                  <div className="pl-11 pr-2 pb-3">
                    {results.address.is_valid && results.address.standardized_data && (
                      <div className="p-3 border border-green-200 bg-green-50 rounded-md">
                        <p className="text-xs font-medium text-green-700 uppercase mb-2">
                          USPS STANDARDIZED ADDRESS
                        </p>
                        <p className="text-sm text-green-900">
                          {results.address.standardized_data.street1}
                          {results.address.standardized_data.street2 &&
                            `, ${results.address.standardized_data.street2}`}
                        </p>
                        <p className="text-sm text-green-900">
                          {results.address.standardized_data.city},{' '}
                          {results.address.standardized_data.state}{' '}
                          {results.address.standardized_data.zip_code}
                        </p>
                        {onDataUpdated && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              handleAcceptStandardized('address', results.address?.standardized_data)
                            }
                            className="mt-2"
                          >
                            <Pencil className="h-3 w-3 mr-2" />
                            Use Standardized Address
                          </Button>
                        )}
                      </div>
                    )}
                    {results.address.warnings?.map((warning, i) => (
                      <Alert key={i} className="mt-2 border-yellow-200 bg-yellow-50">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <AlertDescription className="text-yellow-800">{warning}</AlertDescription>
                      </Alert>
                    ))}
                    {results.address.errors?.map((error, i) => (
                      <Alert key={i} variant="destructive" className="mt-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}
                <hr className="border-border" />
              </>
            )}

            {/* Phone Validation */}
            {member.phone_number && (
              <>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-0.5">
                      {results.phone ? (
                        getStatusIcon(results.phone.is_valid, results.phone.confidence_score)
                      ) : (
                        <Phone className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">Phone</p>
                      <p className="text-sm text-muted-foreground">{member.phone_number}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={validatePhone}
                    disabled={loading.phone}
                  >
                    {loading.phone ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Phone className="h-4 w-4 mr-2" />
                    )}
                    {results.phone ? 'Re-validate' : 'Validate'}
                  </Button>
                </div>

                {results.phone && (
                  <div className="pl-11 pr-2 pb-3">
                    {results.phone.details && (
                      <div className="p-3 border border-border bg-muted rounded-md">
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <p className="text-xs text-muted-foreground">Carrier</p>
                            <p className="text-sm">{results.phone.details.carrier || 'Unknown'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Type</p>
                            <p className="text-sm">{results.phone.details.line_type || 'Unknown'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Country</p>
                            <p className="text-sm">{results.phone.details.country_code || 'US'}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {results.phone.warnings?.map((warning, i) => (
                      <Alert key={i} className="mt-2 border-yellow-200 bg-yellow-50">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <AlertDescription className="text-yellow-800">{warning}</AlertDescription>
                      </Alert>
                    ))}
                    {results.phone.errors?.map((error, i) => (
                      <Alert key={i} variant="destructive" className="mt-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}
                <hr className="border-border" />
              </>
            )}

            {/* Email Validation */}
            {member.email && (
              <>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="mt-0.5">
                      {results.email ? (
                        getStatusIcon(results.email.is_valid, results.email.confidence_score)
                      ) : (
                        <Mail className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">Email</p>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={validateEmail}
                    disabled={loading.email}
                  >
                    {loading.email ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    {results.email ? 'Re-validate' : 'Validate'}
                  </Button>
                </div>

                {results.email && (
                  <div className="pl-11 pr-2 pb-3">
                    {results.email.details && (
                      <div className="p-3 border border-border bg-muted rounded-md">
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <p className="text-xs text-muted-foreground">Domain Valid</p>
                            <p className="text-sm">
                              {results.email.details.domain_valid ? 'Yes' : 'No'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">MX Records</p>
                            <p className="text-sm">
                              {results.email.details.mx_records ? 'Found' : 'Not Found'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Disposable</p>
                            <p className="text-sm">
                              {results.email.details.is_disposable ? 'Yes' : 'No'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {results.email.warnings?.map((warning, i) => (
                      <Alert key={i} className="mt-2 border-yellow-200 bg-yellow-50">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <AlertDescription className="text-yellow-800">{warning}</AlertDescription>
                      </Alert>
                    ))}
                    {results.email.errors?.map((error, i) => (
                      <Alert key={i} variant="destructive" className="mt-2">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Missing Data Warning */}
        {expanded && !member.address && !member.phone_number && !member.email && (
          <Alert className="border-blue-200 bg-blue-50">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              No contact information available to validate. Add address, phone, or email to enable
              validation.
            </AlertDescription>
          </Alert>
        )}

        {/* Data Quality Tips */}
        {expanded && Object.keys(results).length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-900">
              <strong>Tip:</strong> Validated contact information improves engagement success rates
              by up to 40%. Use standardized addresses for reliable mail delivery.
            </p>
          </div>
        )}

        {/* Update Confirmation Dialog */}
        <Dialog open={showUpdateDialog} onClose={() => setShowUpdateDialog(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Member Data?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Would you like to update the member's {pendingUpdate?.field} with the validated data?
              This will replace the current value.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={confirmUpdate}>Update</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default MemberDataValidation;
